// Lib
import React, { useState, useLayoutEffect, useMemo, useRef } from 'react';
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { inRange } from 'lodash/fp';

// Utils
import { getHeight, getWidth } from '../../../../../common/maths/geometry/rect';
import { getElementModalSize } from '../../../modal/elementModalUtils';
import {
    getCaption,
    getElementId,
    getHasDrawing,
    getShowCaption,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import {
    getImageEditorDataObject,
    getLargestImageSize,
    getOriginalImage,
    getOriginalImageWidth,
    getOriginalImageHeight,
} from '../imageModalUtils';
import { getImageSource } from '../../imageHelper';
import getFilenameFromUrl from '../../../../../common/files/getFilenameFromUrl';
import { appendToFilename } from '../imageModalFileUtils';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../feature/elementFeatureSelector';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import Spinner from '../../../../components/loaders/Spinner';
import ElementImage from '../../../../components/images/ElementImage';
import Caption from '../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../drawing/DrawingSvg';
import ImageModalToolbarSecondaryTools from '../toolbar/ImageModalToolbarSecondaryTools';
import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_FOOTER_SIZE } from '../imageModalConstants';
import { IMAGE_TYPES } from '../../../../../common/media/mediaConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Styles
import './ImageModalEditor.scss';

// Hooks
import { useImageRotation } from './hooks/useImageRotation';
import { useCropSaveHandler } from './hooks/useCropSaveHandler';
import { useImageModalEditorImageLoader, IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY } from './hooks/useImageModalEditorImageLoader';
import { useBackgroundRemovalCrop } from './hooks/useBackgroundRemovalCrop';
import { adjustCropperForRotation } from './utils/cropperUtils';
import { useTranslator } from '../../../../components/i18n/useTranslator';
import { getPersistedImageEditorTransform } from './utils/imageModalEditorDataAdapterUtils';
import { isImageModalEditorVariantSwap, type ImageModalEditorSourceIdentity } from './utils/imageModalEditorSourceUtils';

export type ImageModalEditorProps = {
    element: ImMNElement;
    gridSize: number;
    attachment: unknown;
    windowWidth: number;
    windowHeight: number;
    idealHeight: number;
    cancelEditing: () => void;
    // `didSave` is true only when a changed image was persisted; a clean no-op close passes false to exit
    // immediately.
    afterSaveCb: (didSave?: boolean) => void;
};

/**
 * V1 image crop/rotate editor: plain Rotate/Cancel/Save toolbar, always edits the full original source,
 * and clears+reloads the Cropper image rather than holding a frame across a source swap.
 */
const ImageModalEditor: React.FC<ImageModalEditorProps> = ({
    element,
    gridSize,
    attachment,
    windowWidth,
    windowHeight,
    idealHeight,
    cancelEditing,
    afterSaveCb,
}) => {
    const translate = useTranslator('popup', 'imageModal.editor');
    const useSecureMediaUrl = true;

    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );

    const {
        backgroundRemovalEditSource,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    } = useBackgroundRemovalCrop({
        element,
        elementId: getElementId(element),
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    const [editorIsReady, setEditorIsReady] = useState(false);
    const [storedEditorData] = useState(() => getImageEditorDataObject(element));
    const storedTransform = useMemo(() => getPersistedImageEditorTransform(storedEditorData), [storedEditorData]);
    const initialRotation = storedTransform?.rotation || 0;
    const initialScaleX = storedTransform?.scaleX || 1;
    const initialScaleY = storedTransform?.scaleY || 1;
    const [currentRotation, setCurrentRotation] = useState<number>(initialRotation);
    const [currentScaleX, setCurrentScaleX] = useState<number>(initialScaleX);
    const [currentScaleY, setCurrentScaleY] = useState<number>(initialScaleY);
    const [hideDrawing, setHideDrawing] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    // V1 always edits the full original source (or its background-removal-edit variant) — there is no
    // crop/edited-render toggle, unlike V2.
    const sourceImage = backgroundRemovalEditSource || getOriginalImage(element);

    const imageRef = useRef<HTMLImageElement | null>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const pendingTransformRef = useRef<(() => void) | null>(null);
    const isDirtyRef = useRef(false);
    const initialCropDataRef = useRef<Cropper.CropBoxData | null>(null);
    // Tracks the image currently loaded so a background-removal source swap (same element, different source
    // URL) can be told apart from a genuinely new image.
    const previousImageIdentityRef = useRef<ImageModalEditorSourceIdentity | null>(null);
    // Live rotate/flip/crop snapshotted before a background-removal source swap, re-applied once the swapped-in
    // image loads so the user's in-progress edits survive the reload.
    const pendingCropperDataRef = useRef<Cropper.Data | null>(null);

    const elementId = getElementId(element);
    const caption = getShowCaption(element) && getCaption(element);
    const originalImageWidth = getWidth(sourceImage) || getOriginalImageWidth({ element, gridSize, attachment });
    const originalImageHeight = getHeight(sourceImage) || getOriginalImageHeight({ element, gridSize, attachment });

    const isRotatedOnSide = !!currentRotation && currentRotation !== -180;

    // Flip the image width and height if it's rotated onto its side
    const imageWidth = isRotatedOnSide ? originalImageHeight : originalImageWidth;
    const imageHeight = isRotatedOnSide ? originalImageWidth : originalImageHeight;

    const { modalWidth, modalHeight } = getElementModalSize({
        windowWidth,
        windowHeight,
        imageWidth,
        imageHeight,
        idealHeight,
        caption,
        footerSize: IMAGE_MODAL_FOOTER_SIZE,
        modalMinWidth: 0,
        modalMinHeight: 0,
        gridSize,
        modalVerticalMarginGU: 5,
    });

    const svgWrapperStyle = {
        width: '100%',
        height: '100%',
        // Mirror the image flip on the drawing overlay. Use the delta from the saved scale so the overlay
        // sits in its stored orientation on open (delta 1) and only flips with new edits. Rotation is excluded
        // because the rotated drawing is hidden, not transformed.
        transform: `scaleX(${currentScaleX / initialScaleX}) scaleY(${currentScaleY / initialScaleY})`,
        transformOrigin: 'center',
    };

    const loadingImageStyles = {
        width: isRotatedOnSide ? modalHeight : modalWidth,
        height: isRotatedOnSide ? modalWidth : modalHeight,
        transform: `rotate(${initialRotation}deg)`,
    };

    const sourceImageUrl = getImageSource({
        imageDetails: sourceImage,
        imageSize: getLargestImageSize(sourceImage),
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    const editorImageLoadKey = `${elementId}-${imageType}-${initialRotation}-${initialScaleX}-${initialScaleY}-${sourceImageUrl || ''}`;

    const hasDrawing = getHasDrawing(element);

    const { handleRotateImage } = useImageRotation({
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        currentScaleX,
        currentScaleY,
        setCurrentRotation,
        setCurrentScaleX,
        setCurrentScaleY,
        setHideDrawing,
    });

    const { handleSave } = useCropSaveHandler({
        cropperRef,
        isDirtyRef,
        elementId,
        cropSourceImage: sourceImage,
        imageType,
        largestUneditedImageUrl: sourceImageUrl,
        hideDrawing,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        shouldSaveWhenClean: false,
        getRevertedImage: getRevertImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        afterSaveCb,
    });

    useLayoutEffect(() => {
        const nextIdentity = {
            elementId,
            imageType,
            url: sourceImageUrl,
            usesOriginalSource: true,
        };
        const isVariantSwap = isImageModalEditorVariantSwap(previousImageIdentityRef.current, nextIdentity);
        previousImageIdentityRef.current = nextIdentity;

        if (isVariantSwap) {
            // Snapshot the live cropper state before the reload tears the cropper down. It's re-applied in
            // loadCanvasIntoCropper's ready callback once the swapped-in image is loaded.
            pendingCropperDataRef.current = cropperRef.current?.getData() ?? null;
            setEditorIsReady(false);
            return;
        }

        pendingCropperDataRef.current = null;
        setCurrentRotation(initialRotation);
        setCurrentScaleX(initialScaleX);
        setCurrentScaleY(initialScaleY);
        setHideDrawing(false);
        setShowWarningDialog(false);
        pendingTransformRef.current = null;
        setEditorIsReady(false);
        isDirtyRef.current = false;
        initialCropDataRef.current = null;
    }, [elementId, imageType, initialRotation, initialScaleX, initialScaleY, sourceImageUrl]);

    useImageModalEditorImageLoader({
        imageRef,
        cropperRef,
        isDirtyRef,
        element,
        imageType,
        loadKey: editorImageLoadKey,
        loadStrategy: IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.CLEAR_IMAGE_BEFORE_LOAD,
        sourceUrl: sourceImageUrl,
        onEditorReadyChange: setEditorIsReady,
        applyStoredCropData: true,
        pendingCropperDataRef,
        recenterAfterTransforms: true,
    });

    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (!editorIsReady || !cropper || !initialCropDataRef.current) return;

        adjustCropperForRotation(cropper, modalWidth, initialCropDataRef.current);
    }, [currentRotation, editorIsReady, modalWidth]);

    // V1 cannot follow a drawing through a quarter-turn, so warn and clear it rather than trying to keep it
    // in sync with the rotation.
    const handleRotateClick = () => {
        if (!hideDrawing && hasDrawing) {
            pendingTransformRef.current = () => {
                handleRotateImage();
            };
            setShowWarningDialog(true);
            return;
        }

        handleRotateImage();
    };

    const handleConfirmRotation = () => {
        setShowWarningDialog(false);
        pendingTransformRef.current?.();
        pendingTransformRef.current = null;
    };

    const handleCancelRotation = () => {
        setShowWarningDialog(false);
        pendingTransformRef.current = null;
    };

    const showEditorLoadingPreview = !editorIsReady;
    const toolbarDisabled = showWarningDialog || !editorIsReady;

    return (
        <>
            <div className="image-modal-content">
                <div className="image-container">
                    <div className="ImageEditor">
                        <div
                            className={classNames('editor', { loading: showEditorLoadingPreview })}
                            style={{ width: modalWidth, height: modalHeight }}
                        >
                            {showEditorLoadingPreview && (
                                <div className="loading-message">
                                    <div
                                        className="loading-image"
                                        style={{
                                            ...loadingImageStyles,
                                            width: isRotatedOnSide ? modalHeight : modalWidth,
                                            height: isRotatedOnSide ? modalWidth : modalHeight,
                                        }}
                                    >
                                        <ElementImage
                                            useSecureMediaUrl={useSecureMediaUrl}
                                            showBrokenIconOnError
                                            element={element}
                                            imageData={sourceImage}
                                            imageType={IMAGE_TYPES.ELEMENT}
                                            widthPx={modalWidth}
                                            key={`${elementId}-${sourceImageUrl}`}
                                        />
                                    </div>
                                    <SimpleDrawingSvg element={element} />
                                    <div className="loading-overlay">
                                        <Spinner show />
                                        {translate('loadingEditor')}…
                                    </div>
                                </div>
                            )}

                            <img
                                ref={(c) => {
                                    imageRef.current = c;
                                }}
                                className="editor-source-image"
                                style={{ width: modalWidth, height: modalHeight }}
                            />
                            {hasDrawing && !hideDrawing && editorIsReady && (
                                <div className="drawing-svg-container">
                                    <div className="drawing-wrapper" style={svgWrapperStyle}>
                                        <SimpleDrawingSvg element={element} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Caption
                        element={element}
                        textContent={getCaption(element)}
                        captionVisible={getShowCaption(element)}
                    />
                    {showWarningDialog && (
                        <div className="warning-dialog">
                            <div className="dialog-body">
                                <div className="dialog-text">
                                    <span className="warning-label">{translate('warningLabel')}</span>
                                    &nbsp;
                                    {translate('warningMessage')}
                                </div>

                                <div className="buttons">
                                    <Button className="StyledButton secondary" onClickFn={handleCancelRotation}>
                                        {translate('cancel')}
                                    </Button>
                                    <Button className="StyledButton danger" onClickFn={handleConfirmRotation}>
                                        {translate('continueAnyway')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ImageModalToolbarSecondaryTools className="ImageEditorModalToolbar">
                <Button
                    className="StyledButton secondary image-control rotate"
                    onClickFn={handleRotateClick}
                    disabled={toolbarDisabled}
                >
                    <Icon name="modal-toolbar-rotate" />
                    {translate('rotateLeft')}
                </Button>

                <Button className="StyledButton secondary cancel no-hover" onClickFn={cancelEditing}>
                    {translate('cancel')}
                </Button>

                <Button className="StyledButton primary save" onClickFn={handleSave} disabled={toolbarDisabled}>
                    {translate('save')}
                </Button>
            </ImageModalToolbarSecondaryTools>
        </>
    );
};

export default ImageModalEditor;
